import jsPDF from 'jspdf';
import { receiptNumberFor, formatCardBrand, receiptTotalsFor, receiptTargetingRow } from '../components/self-service/orderReceiptHelpers';
import {
  type PublisherConfig,
  type SelfServiceSubmissionRow,
} from '../types/selfService';
import {
  buildMonthlyReceiptSchedule,
  buildUpfrontPayments,
  paymentStatusLabel,
  type ReceiptExtensionCharge,
} from '../components/self-service/receiptPayments';
import { formatDate, formatDateTime } from '../components/self-service/shared/dateFormat';
import { formatMoney } from '../components/self-service/shared/format';

// Page geometry (mm). A4 portrait is 210 × 297.
const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN_X = 18;
const MARGIN_TOP = 20;
const MARGIN_BOTTOM = 20;
const CONTENT_W = PAGE_W - MARGIN_X * 2;
const PAGE_USABLE_BOTTOM = PAGE_H - MARGIN_BOTTOM;

// Slate-ish gray for muted text/lines. jsPDF setTextColor takes RGB ints.
const COLOR_TEXT = [15, 23, 42] as const;       // slate-900
const COLOR_MUTED = [100, 116, 139] as const;   // slate-500
const COLOR_LINE = [226, 232, 240] as const;    // slate-200

export interface ReceiptPdfData {
  row: SelfServiceSubmissionRow;
  config: PublisherConfig;
  // CTV-516: upfront extension charges from the submission's edit log, listed
  // as discrete payment rows. Monthly extensions surface through the live
  // installment schedule instead. Omitted where there are no edits.
  extensionCharges?: ReceiptExtensionCharge[];
}

// Render the receipt as a vector PDF (jsPDF text APIs only — no raster
// capture). Output: ~10-30KB, selectable text, infinite zoom fidelity.
export async function exportReceiptToPdf(data: ReceiptPdfData, filename: string): Promise<void> {
  const { row, config, extensionCharges } = data;
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
    compress: true,
  });

  let y = MARGIN_TOP;

  // --- Header: publisher name (left) + receipt # / date (right) ----------
  pdf.setTextColor(...COLOR_TEXT);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(16);
  pdf.text(config.name, MARGIN_X, y);

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.setTextColor(...COLOR_MUTED);
  pdf.text('Order Receipt', MARGIN_X, y + 5);

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.setTextColor(...COLOR_TEXT);
  const receiptNo = receiptNumberFor(row.id);
  pdf.text(receiptNo, PAGE_W - MARGIN_X, y, { align: 'right' });

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.setTextColor(...COLOR_MUTED);
  pdf.text(formatDate(row.created_at), PAGE_W - MARGIN_X, y + 5, { align: 'right' });

  y += 10;
  y = drawDivider(pdf, y);

  // --- Two-column block: Bill to / Issued by -----------------------------
  const colW = CONTENT_W / 2;
  const billLines = [
    row.business_info?.businessName?.trim() || '—',
    row.business_info?.contactName?.trim() || null,
    row.contact_email?.trim() || null,
    row.business_info?.contactPhone?.trim() || null,
  ].filter((s): s is string => Boolean(s));
  // CTV-518: CTVBuyer is the merchant of record — it issues the receipt on
  // the station's behalf. Station branding stays in the header above.
  const issuerLines = [
    'CTVBuyer',
    `on behalf of ${config.name}`,
  ].filter((s): s is string => Boolean(s));

  drawSectionTitle(pdf, 'Bill To', MARGIN_X, y);
  drawSectionTitle(pdf, 'Issued By', MARGIN_X + colW, y);
  y += 5;

  pdf.setFontSize(10);
  pdf.setTextColor(...COLOR_TEXT);
  const blockHeight = Math.max(billLines.length, issuerLines.length) * 4.5;
  billLines.forEach((line, i) => {
    pdf.setFont('helvetica', i === 0 ? 'bold' : 'normal');
    pdf.text(line, MARGIN_X, y + i * 4.5);
  });
  issuerLines.forEach((line, i) => {
    pdf.setFont('helvetica', i === 0 ? 'bold' : 'normal');
    pdf.text(line, MARGIN_X + colW, y + i * 4.5);
  });

  y += blockHeight + 4;
  y = drawDivider(pdf, y);

  // --- Campaign details --------------------------------------------------
  drawSectionTitle(pdf, 'Campaign', MARGIN_X, y);
  y += 5;

  const dateRange =
    row.campaign?.startDate && row.campaign?.endDate
      ? `${formatDate(row.campaign.startDate)} – ${formatDate(row.campaign.endDate)}`
      : '—';
  // CTV-674: same summary the on-screen OrderReceipt renders (previously the
  // PDF ignored picked markets for single-market publishers and always
  // printed "Channel").
  const { label: marketsRowLabel, value: marketsRowValue } = receiptTargetingRow(row, config);
  const planLabel = row.payment_plan === 'monthly' ? 'Monthly' : 'Upfront';
  const campaignLabel = row.campaign?.campaignName?.trim() || row.business_info?.businessName || '—';

  y = drawLabelValueRow(pdf, 'Name', campaignLabel, y);
  y = drawLabelValueRow(pdf, 'Dates', dateRange, y);
  y = drawLabelValueRow(pdf, marketsRowLabel, marketsRowValue, y);
  y = drawLabelValueRow(pdf, 'Payment Plan', planLabel, y);

  y += 2;
  y = drawDivider(pdf, y);

  // --- Payment summary ---------------------------------------------------
  drawSectionTitle(pdf, 'Payment Summary', MARGIN_X, y);
  y += 5;

  // CTV-518: totals always render backend cents (see receiptTotalsFor) —
  // media spend = budget, total = budget + tax + service fee. Monthly rows
  // with live installments sum the backend's per-installment cents.
  const { total, serviceFee: fee, tax, mediaSpend } = receiptTotalsFor(row);

  y = drawAmountRow(pdf, 'Media Spend', formatMoney(mediaSpend), y, false);
  // Fee row renders only when the stored row carries a non-zero service
  // fee (pre-CTV-635 history); every charge since then stores 0.
  if (fee > 0) {
    y = drawAmountRow(pdf, 'Service fee', formatMoney(fee), y, false);
  }
  if (tax > 0) {
    y = drawAmountRow(pdf, 'Tax', formatMoney(tax), y, false);
  }
  // Subtotal rule, then breathing room for the bold total. The 11pt bold
  // font's ascent is ~3.5mm, so we need at least 5mm clearance below the
  // divider to keep the bold text from kissing the line above.
  y += 1.5;
  pdf.setDrawColor(...COLOR_LINE);
  pdf.setLineWidth(0.2);
  pdf.line(MARGIN_X, y, MARGIN_X + CONTENT_W, y);
  y += 6;

  // Mirror OrderReceipt: prefer the backend's live installment rows (post-
  // extension state); fall back to splitting the budget and grossing up each
  // charge when no installment rows exist yet.
  const isMonthly = row.payment_plan === 'monthly';
  // CTV-516: same ledger model the on-screen receipt uses.
  // CTV-518: amounts come from row.installments (backend MoR gross-up).
  const schedule = isMonthly ? buildMonthlyReceiptSchedule(row) : [];
  const upfrontPayments = isMonthly ? null : buildUpfrontPayments(row, extensionCharges ?? []);
  const hasUpfrontExtensions = (upfrontPayments?.rows.length ?? 0) > 1;

  // CTV-658: post-approval, heading + monthly sub-line stop speaking in
  // at-submission ("authorized", "after approval") terms — mirrors
  // OrderReceipt.tsx.
  y = drawAmountRow(
    pdf,
    isMonthly
      ? 'Total Commitment'
      : row.status === 'approved'
        // With extensions the Payments ledger owns "Total Paid"; the summary
        // line is only the first charge.
        ? hasUpfrontExtensions
          ? 'Initial Payment'
          : 'Total Paid'
        : 'Total Authorized',
    formatMoney(total),
    y,
    true,
  );

  // Sub-line clarifying when the total actually moves money.
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8.5);
  pdf.setTextColor(...COLOR_MUTED);
  const totalSubLine = isMonthly
    ? row.status === 'approved'
      ? `Billed across ${schedule.length || 'multiple'} monthly payments — see the schedule below for what's been charged and what's coming.`
      : `No charge today. Billed across ${schedule.length || 'multiple'} payments after approval — see schedule below.`
    : upfrontSummaryNote(row.status, row.reviewed_at);
  pdf.text(totalSubLine, MARGIN_X, y);
  y += 4;

  // Card brand + last4 line, when Stripe has populated them.
  const cardBrand = row.payment_method_brand;
  const cardLast4 = row.payment_method_last4;
  if (cardBrand && cardLast4) {
    pdf.setFontSize(8.5);
    pdf.setTextColor(...COLOR_TEXT);
    pdf.text(
      `${isMonthly ? 'Card on file:' : 'Paid with'} ${formatCardBrand(cardBrand)} ending in ${cardLast4}`,
      MARGIN_X,
      y,
    );
    y += 4;
  }

  pdf.setFontSize(10);
  pdf.setTextColor(...COLOR_TEXT);
  y += 1;

  // --- Upfront payments ledger (initial + extensions) --------------------
  if (!isMonthly && hasUpfrontExtensions && upfrontPayments) {
    y = ensurePage(pdf, y, 20);
    y += 4;
    y = drawDivider(pdf, y);
    drawSectionTitle(pdf, 'Payments', MARGIN_X, y);
    y += 6;
    pdf.setFontSize(10);
    pdf.setTextColor(...COLOR_TEXT);
    for (const p of upfrontPayments.rows) {
      y = ensurePage(pdf, y, 9.5);
      const subLine = p.date
        ? `${paymentStatusLabel(p.status)} · ${formatDate(p.date)}`
        : paymentStatusLabel(p.status);
      pdf.setFont('helvetica', 'normal');
      pdf.text(p.label, MARGIN_X, y);
      pdf.text(`$${formatMoney(p.amountCents / 100)}`, MARGIN_X + CONTENT_W, y, { align: 'right' });
      pdf.setFontSize(8.5);
      pdf.setTextColor(...COLOR_MUTED);
      pdf.text(subLine, MARGIN_X, y + 3.5);
      pdf.setFontSize(10);
      pdf.setTextColor(...COLOR_TEXT);
      y += 9.5;
    }
    y += 1.5;
    pdf.setDrawColor(...COLOR_LINE);
    pdf.setLineWidth(0.2);
    pdf.line(MARGIN_X, y, MARGIN_X + CONTENT_W, y);
    y += 6;
    y = drawAmountRow(pdf, 'Total Paid', formatMoney(upfrontPayments.totalCents / 100), y, true);
  }

  // --- Payment schedule (monthly only) -----------------------------------
  if (isMonthly) {
    if (schedule.length > 0) {
      // Section header + intro line — pre-allocate ~20mm so we don't
      // page-break right after the heading.
      y = ensurePage(pdf, y, 20);
      y += 4;
      y = drawDivider(pdf, y);
      drawSectionTitle(pdf, 'Payment Schedule', MARGIN_X, y);
      y += 4;
      pdf.setFontSize(8.5);
      pdf.setTextColor(...COLOR_MUTED);
      pdf.text(
        'First payment is due on the campaign start date; subsequent payments charge on each anniversary date.',
        MARGIN_X,
        y,
      );
      y += 7;
      pdf.setFontSize(10);
      pdf.setTextColor(...COLOR_TEXT);
      for (const s of schedule) {
        // Each row is ~13mm tall (three lines: month, coverage, status/date);
        // break to a fresh page when there isn't room.
        y = ensurePage(pdf, y, 13);
        const labelLine = `${s.label}`;
        const subLine = `${s.coversLabel}${s.isPartial ? ' (prorated)' : ''}`;
        const statusLine = s.date
          ? `${s.status === 'paid' ? 'Paid' : 'Due'} ${formatDate(s.date)} · ${paymentStatusLabel(s.status)}`
          : paymentStatusLabel(s.status);
        pdf.setFont('helvetica', 'normal');
        pdf.text(labelLine, MARGIN_X, y);
        pdf.text(`$${formatMoney(s.amountCents / 100)}`, MARGIN_X + CONTENT_W, y, { align: 'right' });
        pdf.setFontSize(8.5);
        pdf.setTextColor(...COLOR_MUTED);
        pdf.text(subLine, MARGIN_X, y + 3.5);
        pdf.text(statusLine, MARGIN_X, y + 7);
        pdf.setFontSize(10);
        pdf.setTextColor(...COLOR_TEXT);
        y += 13;
      }
    }
  }

  y += 2;
  y = drawDivider(pdf, y);

  // --- Authorization notice ----------------------------------------------
  // The notice block can be ~25-30mm tall (monthly variant is multi-line);
  // pre-allocate so the title + first body line don't split across pages.
  y = ensurePage(pdf, y, 30);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(9);
  pdf.setTextColor(...COLOR_TEXT);
  // CTV-658: post-approval the at-submission framing is false (upfront was
  // captured; monthly installments keep charging regardless of later swap
  // reviews) — mirrors OrderReceipt.tsx.
  pdf.text(row.status === 'approved' ? 'Payment Notice' : 'Authorization Notice', MARGIN_X, y);
  y += 4;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(...COLOR_MUTED);
  const noticeText = isMonthly
    ? row.status === 'approved'
      ? "Payments charge to the same card on each anniversary date per the schedule above. If a monthly charge is declined, we'll retry it once; if it still fails, that month's ads stop running until you update your card and retry from your campaign page."
      : "By submitting, you've agreed to pay according to the schedule above. The first payment is " +
        'due on the campaign start date after approval; subsequent payments will charge to the same ' +
        "card on each anniversary date. If the commercial is rejected, no charges will be made. If a future monthly charge is declined, we'll retry it once; if it still fails, that month's ads stop running until you update your card and retry from your campaign page."
    : row.status === 'approved'
      ? 'Payment was captured when your campaign was approved.'
      : 'Card authorized for the total above. You will only be charged once your commercial is ' +
        'approved. If the commercial is rejected, the hold is released and no charge will be made.';
  const noticeLines = pdf.splitTextToSize(noticeText, CONTENT_W);
  pdf.text(noticeLines, MARGIN_X, y);
  y += noticeLines.length * 4 + 3;

  // --- Footer fine print -------------------------------------------------
  pdf.setFontSize(8);
  pdf.setTextColor(...COLOR_MUTED);
  const footerParts: string[] = [
    'Charges appear as CTVBuyer on your card statement.',
    `Generated ${formatDateTime(new Date().toISOString())}.`,
  ];
  const footerLines = pdf.splitTextToSize(footerParts.join(' '), CONTENT_W);
  // Footer is usually 1-2 lines (~6mm); reserve room or push to a new page.
  y = ensurePage(pdf, y, footerLines.length * 3.5 + 2);
  pdf.text(footerLines, MARGIN_X, y);

  pdf.save(filename);
}

// Single-line label/value row. The value wraps to multiple lines if it
// overflows the value column; returns the next baseline y. The trailing
// +1.5mm adds inter-row breathing room without affecting jsPDF's internal
// line wrapping inside a multi-line value.
function drawLabelValueRow(pdf: jsPDF, label: string, value: string, y: number): number {
  const labelW = 35;
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(...COLOR_MUTED);
  pdf.text(label, MARGIN_X, y);
  pdf.setTextColor(...COLOR_TEXT);
  const valueLines = pdf.splitTextToSize(value, CONTENT_W - labelW);
  pdf.text(valueLines, MARGIN_X + labelW, y);
  return y + Math.max(1, valueLines.length) * 4.5 + 1.5;
}

function drawAmountRow(
  pdf: jsPDF,
  label: string,
  amount: string,
  y: number,
  emphasize: boolean,
): number {
  pdf.setFontSize(emphasize ? 11 : 10);
  pdf.setFont('helvetica', emphasize ? 'bold' : 'normal');
  pdf.setTextColor(...COLOR_TEXT);
  pdf.text(label, MARGIN_X, y);
  pdf.text(`$${amount}`, MARGIN_X + CONTENT_W, y, { align: 'right' });
  return y + (emphasize ? 6 : 6.5);
}

// Add a new page and reset the y cursor when drawing `neededHeight` more
// would overflow the bottom margin. Returns the (possibly new) y position
// so callers can pipeline:  y = ensurePage(pdf, y, 8);
function ensurePage(pdf: jsPDF, y: number, neededHeight: number): number {
  if (y + neededHeight <= PAGE_USABLE_BOTTOM) return y;
  pdf.addPage();
  return MARGIN_TOP;
}

function drawSectionTitle(pdf: jsPDF, label: string, x: number, y: number): void {
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(7.5);
  pdf.setTextColor(...COLOR_MUTED);
  pdf.text(label.toUpperCase(), x, y, { charSpace: 0.4 });
}

function drawDivider(pdf: jsPDF, y: number): number {
  pdf.setDrawColor(...COLOR_LINE);
  pdf.setLineWidth(0.2);
  pdf.line(MARGIN_X, y, MARGIN_X + CONTENT_W, y);
  return y + 4;
}

// Mirrors OrderReceipt.upfrontSummaryNote — keep the two in sync so the PDF
// reads identically to the on-screen receipt.
function upfrontSummaryNote(
  status: SelfServiceSubmissionRow['status'],
  reviewedAt: string | null,
): string {
  switch (status) {
    case 'approved':
      return reviewedAt ? `Charged ${formatDate(reviewedAt)}.` : 'Charged on approval.';
    case 'payment_authorized':
      return 'Authorized today; charged on approval.';
    case 'payment_failed':
      return 'This payment could not be completed.';
    case 'payment_refunded':
      return reviewedAt ? `Refunded ${formatDate(reviewedAt)}.` : 'Refunded.';
    case 'creative_rejected':
    case 'archived':
    case 'canceled':
      return 'Hold released — not charged.';
    default:
      return 'Awaiting payment.';
  }
}

export function receiptFilenameFor(submissionId: string, createdAt: string | null | undefined): string {
  const datePart = (createdAt ?? new Date().toISOString()).slice(0, 10);
  return `${receiptNumberFor(submissionId)}-${datePart}.pdf`;
}
